import { parseCoordinate, getCoordinatesFromDirection } from '@/utils/coordinate-processor'
import { AppState } from '@/stores/app-state'
import { Toast } from '@/components/toast'
import { ApiService } from '@/services/api'
import type { PlotSettings, ProjectPlotSettings } from '@/types'

export class PlotViewer {
  private container: HTMLElement
  private appState: AppState  
  private toastService: Toast
  private apiService: ApiService
  private coordinates: string[] = []
  private plotSettings: PlotSettings = {
    lineThickness: 3.0,
    strokeColor: '#000000',
    backgroundColor: '#ffffff',
    showGrid: false,
    showLabels: true,  // Default: always show labels
    scale: 1,
    units: 'feet'
  }

  // Zoom and pan state
  private viewState = {
    scale: 1,
    translateX: 0,
    translateY: 0,
    minScale: 0.1,
    maxScale: 10
  }

  private panState = {
    isPanning: false,
    startX: 0,
    startY: 0,
    startTranslateX: 0,
    startTranslateY: 0
  }

  private labelSettings = {
    fontSize: 12,
    customPositions: new Map<number, { x: number, y: number }>(),
    hiddenLabels: new Set<number>() // Track which labels are hidden (show only numbers)
  }

  private arrowSettings = {
    customPosition: null as { x: number, y: number } | null
  }

  private dragState = {
    isDragging: false,
    draggedLabel: null as SVGElement | null,
    dragOffset: { x: 0, y: 0 },
    originalPosition: { x: 0, y: 0 }
  }

  private highlightTimeout: ReturnType<typeof setTimeout> | null = null
  private selectedCoordinateIndex: number | null = null
  private zoomSaveTimeout: ReturnType<typeof setTimeout> | null = null
  private activeEditingIndex: number | null = null
  private saveSettingsTimeout: ReturnType<typeof setTimeout> | null = null



  constructor(
    container: HTMLElement,
    appState: AppState,
    toastService: Toast,
    apiService?: ApiService
  ) {
    this.container = container
    this.appState = appState
    this.toastService = toastService
    this.apiService = apiService || new ApiService()
    
    this.init()
  }

  private async init(): Promise<void> {
    this.setupEventListeners()
    await this.loadPlotSettings()
    this.loadCoordinates()
    this.render()
    
    // Ensure labels are displayed according to settings after initial render
    setTimeout(() => {
      this.applyLabelVisibility()
    }, 100)
  }

  private setupEventListeners(): void {
    // Listen to coordinate updates
    this.appState.subscribe('coordinates', (coords: string[]) => {
      console.log('🎯 PlotViewer received coordinate update:', coords.length, coords)
      this.coordinates = coords
      this.render()
      this.updateConvertButton()
    })

    // Listen to description list updates
    this.appState.subscribe('descriptionList', () => {
      this.updateConvertButton()
    })

    // Listen to project changes to reload settings
    this.appState.subscribe('currentProject', async (project) => {
      if (project) {
        console.log('🔄 Project switched, reloading plot settings for:', project.name)
        await this.loadPlotSettings()
        this.applyLabelVisibility()
      }
    })
  }

  private loadCoordinates(): void {
    console.log('🔄 Loading coordinates from app state...')
    this.coordinates = this.appState.getCoordinates()
    console.log('📍 Loaded coordinates:', this.coordinates.length, this.coordinates)
    
    // Also try to restore from localStorage if empty
    if (this.coordinates.length === 0) {
      const currentProject = this.appState.getCurrentProject()
      if (currentProject) {
        const storedCoords = localStorage.getItem(`coords_${currentProject.id}`)
        if (storedCoords) {
          try {
            const parsed = JSON.parse(storedCoords)
            console.log('🔄 Found stored coordinates, restoring:', parsed.length)
            this.coordinates = parsed
            this.appState.setCoordinates(parsed, true) // Skip persistence to avoid loop
          } catch (error) {
            console.error('Failed to parse stored coordinates:', error)
          }
        }
      }
    }
    
    if (this.coordinates.length > 0) {
      console.log('✅ Will render plot with coordinates:', this.coordinates.length)
      this.render()
    } else {
      console.log('⚠️ No coordinates found, will show empty state')
    }
    
    this.updateConvertButton()
  }

  private updateConvertButton(): void {
    const convertBtn = document.getElementById('convert-to-coords-btn') as HTMLButtonElement
    if (!convertBtn) return

    const descriptionList = this.appState.getDescriptionList()
    const hasCoordinates = this.coordinates.length > 0

    if (hasCoordinates) {
      convertBtn.textContent = '🔄 Re-convert Coordinates'
      convertBtn.disabled = false
    } else if (descriptionList && descriptionList.trim().length > 0) {
      convertBtn.textContent = '📐 Generate Plot'
      convertBtn.disabled = false
    } else {
      convertBtn.textContent = '❌ No Description List'
      convertBtn.disabled = true
    }
  }

  private async loadPlotSettings(): Promise<void> {
    try {
      const currentProject = this.appState.getCurrentProject()
      if (!currentProject) {
        console.log('⚠️ No current project, using default plot settings')
        this.ensureDefaultSettings()
        return
      }

      console.log('🔄 Loading plot settings for project:', currentProject.id)
      const settings: ProjectPlotSettings = await this.apiService.getProjectPlotSettings(currentProject.id)
      
      // Update local settings with robust defaults
      this.plotSettings.lineThickness = settings.line_thickness || 3.0
      this.plotSettings.showLabels = settings.show_labels !== undefined ? settings.show_labels : true // Always default to true
      this.labelSettings.fontSize = settings.label_font_size || 12
      
      // Load custom label positions
      this.labelSettings.customPositions.clear() // Always clear first
      if (settings.custom_label_positions) {
        Object.entries(settings.custom_label_positions).forEach(([key, pos]) => {
          this.labelSettings.customPositions.set(parseInt(key), pos)
        })
        console.log('📍 Loaded custom label positions:', settings.custom_label_positions)
      } else {
        console.log('📍 No custom label positions (using defaults)')
      }
      
      // Load hidden labels
      this.labelSettings.hiddenLabels.clear() // Always clear first
      if (settings.hidden_labels && Array.isArray(settings.hidden_labels)) {
        settings.hidden_labels.forEach(index => {
          this.labelSettings.hiddenLabels.add(index)
        })
        console.log('🫥 Loaded hidden labels:', settings.hidden_labels)
      } else {
        console.log('🫥 No hidden labels (all info visible)')
      }
      
      // Load north arrow position
      this.arrowSettings.customPosition = null // Always clear first
      if (settings.north_arrow_position) {
        this.arrowSettings.customPosition = settings.north_arrow_position
        console.log('🧭 Loaded north arrow position:', settings.north_arrow_position)
      } else {
        console.log('🧭 No custom north arrow position (using default)')
      }
      
      // Load zoom state
      if (settings.zoom_state) {
        this.viewState.scale = settings.zoom_state.scale || 1
        this.viewState.translateX = settings.zoom_state.translateX || 0
        this.viewState.translateY = settings.zoom_state.translateY || 0
        console.log('🔍 Loaded zoom state:', settings.zoom_state)
      } else {
        // Reset to default zoom
        this.viewState.scale = 1
        this.viewState.translateX = 0
        this.viewState.translateY = 0
        console.log('🔍 No saved zoom state (using default)')
      }
      
      console.log('✅ Plot settings loaded:', {
        ...settings,
        showLabels: this.plotSettings.showLabels,
        actualLabelDisplay: this.plotSettings.showLabels ? 'SHOWN' : 'HIDDEN'
      })
    } catch (error) {
      console.error('❌ Failed to load plot settings:', error)
      // Ensure defaults are set even on error
      this.ensureDefaultSettings()
    }
  }

  private ensureDefaultSettings(): void {
    // Ensure robust defaults are always set
    this.plotSettings.showLabels = true
    this.plotSettings.lineThickness = this.plotSettings.lineThickness || 3.0
    this.labelSettings.fontSize = this.labelSettings.fontSize || 12
    this.labelSettings.customPositions.clear()
    this.labelSettings.hiddenLabels.clear() // Empty = all individual labels shown by default
    this.arrowSettings.customPosition = null
    
    console.log('🔄 Default plot settings ensured:', {
      showLabels: this.plotSettings.showLabels,
      lineThickness: this.plotSettings.lineThickness,
      fontSize: this.labelSettings.fontSize,
      hiddenLabelsCount: this.labelSettings.hiddenLabels.size
    })
  }

  private applyLabelVisibility(): void {
    // Apply the current label visibility setting to all labels
    const labels = this.container.querySelectorAll('.label-group')
    labels.forEach(label => {
      ;(label as HTMLElement).style.display = this.plotSettings.showLabels ? 'block' : 'none'
    })
    
    // Update checkbox state to match current settings
    const hideLabelsToggle = this.container.querySelector('#hide-labels-toggle') as HTMLInputElement
    if (hideLabelsToggle) {
      hideLabelsToggle.checked = !this.plotSettings.showLabels // Inverted logic
    }
    
    console.log('👁️ Label visibility applied:', {
      showLabels: this.plotSettings.showLabels,
      labelCount: labels.length,
      display: this.plotSettings.showLabels ? 'VISIBLE' : 'HIDDEN'
    })
  }

  private async savePlotSettings(): Promise<void> {
    try {
      const currentProject = this.appState.getCurrentProject()
      if (!currentProject) {
        console.log('⚠️ No current project, cannot save plot settings')
        return
      }

      // Convert custom positions map to object
      const customPositions: Record<string, { x: number, y: number }> = {}
      this.labelSettings.customPositions.forEach((pos, index) => {
        customPositions[index.toString()] = pos
      })

      // Convert hidden labels set to array
      const hiddenLabelsArray = Array.from(this.labelSettings.hiddenLabels)

      const hasCustomPositions = Object.keys(customPositions).length > 0
      const hasHiddenLabels = hiddenLabelsArray.length > 0
      const settings = {
        line_thickness: this.plotSettings.lineThickness,
        label_font_size: this.labelSettings.fontSize,
        show_labels: this.plotSettings.showLabels,
        custom_label_positions: hasCustomPositions ? customPositions : null,
        hidden_labels: hasHiddenLabels ? hiddenLabelsArray : null,
        north_arrow_position: this.arrowSettings.customPosition,
        zoom_state: {
          scale: this.viewState.scale,
          translateX: this.viewState.translateX,
          translateY: this.viewState.translateY
        }
      }

      console.log(`💾 Saving plot settings - Show Labels: ${this.plotSettings.showLabels ? 'TRUE' : 'FALSE'}, Custom positions: ${hasCustomPositions ? `${Object.keys(customPositions).length} labels` : 'RESET TO NULL'}, Hidden labels: ${hasHiddenLabels ? `${hiddenLabelsArray.length} coordinates` : 'NONE'}`)
      console.log('💾 Full settings object:', settings)
      
      const result = await this.apiService.updateProjectPlotSettings(currentProject.id, settings)
      console.log('✅ Plot settings saved successfully, API response:', {
        ...result,
        showLabels: result.show_labels,
        labelDisplay: result.show_labels ? 'LABELS SHOWN' : 'LABELS HIDDEN'
      })
      
      // Verify the save worked by checking the returned values
      if (result.custom_label_positions === null && !hasCustomPositions) {
        console.log('✅ Reset confirmed: custom_label_positions is null in database')
      }
    } catch (error) {
      console.error('❌ Failed to save plot settings:', error)
      throw error // Re-throw so the calling function knows it failed
    }
  }

  private debouncedSavePlotSettings(): void {
    // Clear any existing timeout
    if (this.saveSettingsTimeout) {
      clearTimeout(this.saveSettingsTimeout)
    }
    
    // Set a new timeout to save after 500ms of inactivity
    this.saveSettingsTimeout = setTimeout(() => {
      this.savePlotSettings().catch((error: any) => {
        console.error('❌ Failed to save plot settings (debounced):', error)
      })
    }, 500)
  }

  private render(): void {
    try {
      console.log('🎨 Rendering PlotViewer with coordinates:', this.coordinates.length)
      
      if (!this.coordinates || this.coordinates.length === 0) {
        // Check if plot generation is in progress
        const isGeneratingPlot = document.querySelector('.loading-modal') !== null
        
        if (isGeneratingPlot) {
          // Show loading state during plot generation
          this.container.innerHTML = `
            <div class="flex items-center justify-center h-64 text-gray-600">
              <div class="text-center">
                <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <div class="text-lg font-medium">Generating Your Plot</div>
                <div class="text-sm mt-2 text-gray-500">
                  Processing descriptions and creating visualization...
                </div>
              </div>
            </div>
          `
        } else {
          // Show empty state when not generating
          this.container.innerHTML = `
            <div class="flex items-center justify-center h-64 text-gray-500">
              <div class="text-center">
                <div class="text-4xl mb-4">📐</div>
                <div>No coordinates available for plotting</div>
                <div class="text-sm mt-2 text-gray-400">
                  Please go back to Step 4 and generate coordinates first
                </div>
              </div>
            </div>
          `
        }
        return
      }

      const points = this.calculateSurveyingPoints()
      if (points.length === 0) {
        console.warn('⚠️ No valid points calculated')
        return
      }

      const bounds = this.calculateBounds(points)
      const viewBoxPadding = 50

      this.container.innerHTML = `
        <div class="plotting-container" style="min-height: calc(100vh - 150px);">
          <!-- Header Controls -->
          <div class="flex justify-between items-center mb-4 bg-white p-4 rounded-lg shadow-sm border">
            <h3 class="text-lg font-semibold text-gray-800">📐 Interactive Plot Visualization</h3>
            <div class="flex items-center space-x-4">
              <label class="flex items-center space-x-2">
                <input type="checkbox" id="hide-labels-toggle" ${!this.plotSettings.showLabels ? 'checked' : ''} class="rounded">
                <span class="text-sm">Hide labels</span>
              </label>
              <div class="flex items-center space-x-2">
                <label class="text-sm">Line:</label>
                <input type="range" id="line-thickness" min="0.5" max="8" step="0.5" value="${this.plotSettings.lineThickness}" class="w-20">
                <span class="text-sm">${this.plotSettings.lineThickness.toFixed(1)}px</span>
              </div>
              <div class="flex items-center space-x-2">
                <label class="text-sm">Labels:</label>
                <input type="range" id="label-size" min="6" max="20" value="${this.labelSettings.fontSize}" class="w-20">
                <span class="text-sm">${this.labelSettings.fontSize}px</span>
              </div>
              
              <!-- Zoom Controls -->
              <div class="flex items-center space-x-2 border-l pl-4">
                <button id="zoom-out" class="btn-icon hover:bg-gray-100" title="Zoom Out">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4"></path>
                  </svg>
                </button>
                <span id="zoom-level" class="text-sm text-gray-600 min-w-12 text-center">${Math.round(this.viewState.scale * 100)}%</span>
                <button id="zoom-in" class="btn-icon hover:bg-gray-100" title="Zoom In">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
                  </svg>
                </button>
                <button id="reset-view" class="btn-icon hover:bg-gray-100" title="Reset View">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                  </svg>
                </button>
              </div>
              
              <button id="reset-labels" class="px-3 py-1 text-sm bg-orange-500 hover:bg-orange-600 text-white rounded border">
                🔄 Reset Labels
              </button>
              <button id="export-plot" class="px-3 py-1 text-sm bg-blue-500 hover:bg-blue-600 text-white rounded border">
                📁 Export SVG
              </button>
              <button id="export-pdf" class="px-3 py-1 text-sm bg-green-500 hover:bg-green-600 text-white rounded border">
                📄 Export PDF
              </button>
            </div>
          </div>

          <!-- Main Content -->
          <div class="flex gap-4">
            <!-- Coordinate List Panel -->
            <div class="w-1/3 bg-white rounded-lg shadow-sm border">
              <div class="p-4 border-b bg-gray-50">
                <h4 class="font-semibold text-gray-800 flex items-center">
                  📋 Metes and Bounds
                  <span class="ml-2 text-xs text-gray-500">Click to highlight line</span>
                </h4>
              </div>
              <div class="coordinate-list-panel flex-1 min-h-0 overflow-y-auto">
                ${this.coordinates.map((coord, index) => {
                  const parsed = parseCoordinate(coord)
                  const isInfoHidden = this.labelSettings.hiddenLabels.has(index)
                  const greenDotColor = isInfoHidden ? 'bg-green-200' : 'bg-green-400'
                  const isSelected = this.selectedCoordinateIndex === index
                  const itemClasses = isSelected 
                    ? 'coordinate-item p-3 border-b bg-blue-100 border-blue-300 transition-colors' 
                    : 'coordinate-item p-3 border-b hover:bg-blue-50 transition-colors'
                  
                  return `
                    <div class="${itemClasses}" data-index="${index}">
                      <div class="flex items-center justify-between">
                        <div class="flex items-center space-x-3 flex-1 cursor-pointer coordinate-click-area">
                          <div class="w-6 h-6 rounded-full ${isSelected ? 'bg-blue-600' : 'bg-blue-500'} text-white text-xs flex items-center justify-center font-bold">
                            ${index + 1}
                          </div>
                          <div class="flex-1">
                            <div class="font-medium ${isSelected ? 'text-blue-900' : 'text-gray-800'}">${this.formatBearing(parsed)}</div>
                            <div class="text-sm ${isSelected ? 'text-blue-700' : 'text-gray-600'}">${this.getCoordinateDistance(parsed)}</div>
                            <!-- Editable coordinate input (hidden by default) -->
                            <input type="text" 
                                   class="coordinate-edit-input w-full p-1 text-sm border border-gray-300 rounded mt-1 hidden" 
                                   value="${coord}" 
                                   data-index="${index}">
                          </div>
                        </div>
                        <div class="flex items-center space-x-2">
                          <!-- IMPROVED UI: Edit button only shows when coordinate is selected -->
                          <button class="edit-coordinate-btn px-3 py-1 text-xs bg-emerald-500 hover:bg-emerald-600 text-white rounded-md shadow-sm transition-all duration-200 ${isSelected ? 'scale-100 opacity-100' : 'scale-75 opacity-0 pointer-events-none'}" 
                                  data-index="${index}" 
                                  title="Edit this coordinate">
                            <span class="flex items-center space-x-1">
                              <span>✏️</span>
                              <span class="font-medium">Edit</span>
                            </span>
                          </button>
                          <!-- Hide Info Button for this specific coordinate -->
                          <button class="hide-label-btn px-2 py-1 text-xs bg-purple-500 hover:bg-purple-600 text-white rounded-md shadow-sm transition-all duration-200 ${isSelected ? 'scale-100 opacity-100' : 'scale-75 opacity-0 pointer-events-none'}" 
                                  data-coord-index="${index}"
                                  title="Hide coordinate info on plot">
                            <span class="flex items-center space-x-1">
                              <span>👁️‍🗨️</span>
                              <span class="text-xs">Hide</span>
                            </span>
                          </button>
                          <div class="w-3 h-3 rounded-full ${greenDotColor}" title="Valid coordinate"></div>
                        </div>
                      </div>
                    </div>
                  `
                }).join('')}
              </div>
            </div>

            <!-- Plot Area -->
            <div class="flex-1 bg-white rounded-lg shadow-sm border p-4">
              <div class="mb-2 text-xs text-gray-500 flex items-center justify-between">
                <span>🖱️ Drag to pan • 🔄 Mouse wheel to zoom • Click coordinates to highlight</span>
                <span>Current zoom: <span id="zoom-level-mini">${Math.round(this.viewState.scale * 100)}%</span></span>
              </div>
              <div class="plot-canvas-container relative" style="min-height: 400px;">
                <svg id="plot-canvas" 
                     viewBox="${bounds.minX - viewBoxPadding} ${bounds.minY - viewBoxPadding} ${bounds.width + 2 * viewBoxPadding} ${bounds.height + 2 * viewBoxPadding}"
                     class="w-full h-full border rounded"
                     style="background: #fafafa; min-height: 400px;">
                  <!-- Content will be added programmatically -->
                </svg>
              </div>
            </div>
          </div>

          <!-- Tract Information -->
          <div class="mt-4 bg-white p-3 rounded-lg shadow-sm border">
            <div class="flex items-center justify-between">
              <h5 class="font-semibold text-gray-800">📊 Survey Summary</h5>
              <div class="flex items-center space-x-6 text-sm">
                <span><strong>Area:</strong> ${this.calculatePolygonArea(points).toFixed(0)} sq ft (${(this.calculatePolygonArea(points) / 43560).toFixed(3)} ac)</span>
                <span><strong>Perimeter:</strong> ${this.calculatePerimeter(points).toFixed(0)} ft</span>
                <span><strong>Segments:</strong> ${points.length}</span>
              </div>
            </div>
          </div>
        </div>
      `

      // Get references and add functionality
      this.addEventListeners()
      this.drawPlot()
      
      // Ensure labels are displayed according to current settings and buttons are updated
      setTimeout(() => {
        this.applyLabelVisibility()
        this.updateHideLabelButton()
        // Apply saved zoom state after plot is rendered
        this.updateTransform()
        this.updateZoomDisplay()
      }, 50)
      
      console.log('✅ PlotViewer rendered successfully')
      
    } catch (error) {
      console.error('❌ Error rendering PlotViewer:', error)
      this.container.innerHTML = `
        <div class="flex items-center justify-center h-64 text-red-500">
          <div class="text-center">
            <div class="text-4xl mb-4">⚠️</div>
            <div>Error rendering plot: ${error instanceof Error ? error.message : 'Unknown error'}</div>
          </div>
        </div>
      `
    }
  }

  private formatBearing(parsed: any): string {
    if (parsed.type === 'line' && parsed.direction && parsed.degrees !== undefined) {
      const dir1 = parsed.direction[0].toUpperCase()
      const dir2 = parsed.direction[1].toUpperCase()
      const degrees = Math.floor(parsed.degrees)
      const minutes = Math.floor((parsed.degrees - degrees) * 60)
      const seconds = Math.floor(((parsed.degrees - degrees) * 60 - minutes) * 60)
      return `${dir1}${degrees}°${minutes.toString().padStart(2, '0')}'${seconds.toString().padStart(2, '0')}"${dir2}`
    } else if (parsed.type === 'curve') {
      const direction = parsed.direction === 'left' ? 'Left' : 'Right'
      const radius = parsed.radius?.toFixed(0) || '0'
      
      // Show curve direction and radius - the core geometry
      return `Curve ${direction} (R=${radius}ft)`
    }
    return 'Unknown'
  }

  private getCoordinateDistance(parsed: any): string {
    if (parsed.type === 'line' && parsed.distance !== undefined) {
      return parsed.distance.toFixed(2)
    } else if (parsed.type === 'curve') {
      // Calculate arc length (the actual distance traveled along the curve)
      const arcLength = parsed.radius && parsed.delta ? (parsed.radius * parsed.delta * Math.PI / 180).toFixed(2) : 'N/A'
      
      if (parsed.chordBearing && parsed.chordLength !== undefined) {
        // For chord-based curves, show arc length and chord info
        const chordBearing = this.formatChordBearing(parsed.chordBearing)
        const chordLength = parseFloat(parsed.chordLength).toFixed(2)
        return `${arcLength}ft arc • Chord ${chordBearing} ${chordLength}ft`
      } else {
        // For standard curves, just show arc length
        return `${arcLength}ft arc`
      }
    }
    return 'N/A'
  }

  private formatChordBearing(chordBearing: string): string {
    // Convert "n06.26.00w" to "N06°26'00"W"
    if (!chordBearing || chordBearing.length < 4) return chordBearing
    
    const dir1 = chordBearing[0].toUpperCase()
    const dir2 = chordBearing[chordBearing.length - 1].toUpperCase()
    const degreesStr = chordBearing.slice(1, -1)
    
    // Parse degrees.minutes.seconds format
    const parts = degreesStr.split('.')
    if (parts.length === 3) {
      const degrees = parts[0]
      const minutes = parts[1].padStart(2, '0')
      const seconds = parts[2].padStart(2, '0')
      return `${dir1}${degrees}°${minutes}'${seconds}"${dir2}`
    }
    
    return chordBearing.toUpperCase()
  }

  private addEventListeners(): void {
    // Coordinate list selection (only for click area, not edit button)
    const coordinateClickAreas = this.container.querySelectorAll('.coordinate-click-area')
    coordinateClickAreas.forEach((area, index) => {
      area.addEventListener('click', () => {
        this.selectCoordinateFromList(index)
      })
    })

    // CRITICAL FIX: Add edit button functionality to left panel
    const editButtons = this.container.querySelectorAll('.edit-coordinate-btn')
    editButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.stopPropagation() // Prevent coordinate selection
        const index = parseInt((button as HTMLElement).dataset.index || '0')
        this.toggleCoordinateEdit(index)
      })
    })

    // Handle coordinate input save on Enter or blur
    const editInputs = this.container.querySelectorAll('.coordinate-edit-input')
    editInputs.forEach((input) => {
      // Prevent coordinate selection when clicking on input field
      input.addEventListener('click', (e) => {
        e.stopPropagation()
        console.log('🎯 Input clicked - prevented coordinate selection')
      })
      
      input.addEventListener('mousedown', (e) => {
        e.stopPropagation()
        console.log('🎯 Input mousedown - prevented coordinate selection')
      })
      
      input.addEventListener('blur', (e) => {
        const index = parseInt((input as HTMLElement).dataset.index || '0')
        const focusEvent = e as FocusEvent
        console.log('🔄 Blur event triggered for input index:', index, 'relatedTarget:', focusEvent.relatedTarget)
        
        // Add a small delay to prevent blur from firing immediately when clicking within input
        setTimeout(() => {
          // Only save if the input is currently visible (not hidden) and not switching to the same input
          if (!input.classList.contains('hidden') && this.activeEditingIndex === index) {
            // Check if we're switching to another input or button within the same coordinate item
            const coordinateItem = input.closest('.coordinate-item')
            const relatedTarget = focusEvent.relatedTarget as HTMLElement
            
            if (relatedTarget && coordinateItem && coordinateItem.contains(relatedTarget)) {
              console.log('⚠️ Blur event ignored - focus staying within same coordinate item')
              return
            }
            
            // Check if we're just entering edit mode (prevent immediate blur)
            if (this.activeEditingIndex === index) {
              console.log('💾 Saving due to blur for index:', index)
              this.saveCoordinateEdit(index, (input as HTMLInputElement).value)
            } else {
              console.log('⚠️ Blur event ignored - not actively editing index:', index)
            }
          } else {
            console.log('⚠️ Blur event ignored - input is hidden or not active for index:', index)
          }
        }, 50) // Small delay to ensure proper event handling
      })
      
      input.addEventListener('focus', (e) => {
        const index = parseInt((input as HTMLElement).dataset.index || '0')
        console.log('🎯 Focus event triggered for input index:', index)
        e.stopPropagation()
      })
      
      input.addEventListener('keydown', (e) => {
        const keyEvent = e as KeyboardEvent
        const index = parseInt((input as HTMLElement).dataset.index || '0')
        console.log('⌨️ Keydown event for input index:', index, 'key:', keyEvent.key)
        
        // Prevent default behavior for arrow keys to allow cursor movement
        if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(keyEvent.key)) {
          e.stopPropagation()
          console.log('⌨️ Arrow key navigation allowed for index:', index)
          return
        }
        
        if (keyEvent.key === 'Enter') {
          console.log('💾 Saving due to Enter key for index:', index)
          e.preventDefault()
          this.saveCoordinateEdit(index, (input as HTMLInputElement).value)
        } else if (keyEvent.key === 'Escape') {
          console.log('❌ Canceling edit due to Escape key for index:', index)
          e.preventDefault()
          this.cancelCoordinateEdit(index)
        }
      })
      
      input.addEventListener('input', (e) => {
        e.stopPropagation()
        console.log('✏️ Input text changed for index:', parseInt((input as HTMLElement).dataset.index || '0'))
      })
    })



    // Hide labels toggle (inverted logic)
    const hideLabelsToggle = this.container.querySelector('#hide-labels-toggle') as HTMLInputElement
    hideLabelsToggle?.addEventListener('change', (e) => {
      const checked = (e.target as HTMLInputElement).checked
      this.plotSettings.showLabels = !checked  // Inverted: checked = hide labels, unchecked = show labels
      this.togglePlotLabels(!checked)
      this.debouncedSavePlotSettings()
      
      console.log('🔄 Label visibility toggled:', {
        checkboxChecked: checked,
        showLabels: this.plotSettings.showLabels,
        actualDisplay: this.plotSettings.showLabels ? 'VISIBLE' : 'HIDDEN'
      })
    })

    // Line thickness control
    const lineThicknessSlider = this.container.querySelector('#line-thickness') as HTMLInputElement
    lineThicknessSlider?.addEventListener('input', (e) => {
      this.plotSettings.lineThickness = parseFloat((e.target as HTMLInputElement).value)
      this.updateLineThickness()
      const thicknessDisplay = lineThicknessSlider.parentElement?.querySelector('span')
      if (thicknessDisplay) {
        thicknessDisplay.textContent = `${this.plotSettings.lineThickness.toFixed(1)}px`
      }
      this.debouncedSavePlotSettings()
    })

    // Label size control
    const labelSizeSlider = this.container.querySelector('#label-size') as HTMLInputElement
    labelSizeSlider?.addEventListener('input', (e) => {
      this.labelSettings.fontSize = parseInt((e.target as HTMLInputElement).value)
      this.updateLabelSize()
      const labelDisplay = labelSizeSlider.parentElement?.querySelector('span')
      if (labelDisplay) {
        labelDisplay.textContent = `${this.labelSettings.fontSize}px`
      }
      this.debouncedSavePlotSettings()
    })

    // Hide/Show label buttons (multiple buttons, one per coordinate)
    const hideLabelButtons = this.container.querySelectorAll('.hide-label-btn') as NodeListOf<HTMLButtonElement>
    hideLabelButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.stopPropagation() // Prevent triggering coordinate selection
        e.preventDefault() // Prevent default button behavior
        const coordIndex = parseInt(button.getAttribute('data-coord-index') || '0')
        console.log('🔄 Individual label button clicked for coordinate:', coordIndex + 1)
        this.toggleSelectedLabel(coordIndex)
      })
    })

    // Reset labels button
    const resetLabelsButton = this.container.querySelector('#reset-labels') as HTMLButtonElement
    resetLabelsButton?.addEventListener('click', async () => {
      await this.resetLabelPositions()
    })

    // Zoom controls
    const zoomInButton = this.container.querySelector('#zoom-in') as HTMLButtonElement
    const zoomOutButton = this.container.querySelector('#zoom-out') as HTMLButtonElement
    const resetViewButton = this.container.querySelector('#reset-view') as HTMLButtonElement
    
    zoomInButton?.addEventListener('click', () => this.zoomIn())
    zoomOutButton?.addEventListener('click', () => this.zoomOut())
    resetViewButton?.addEventListener('click', () => this.resetView())

    // Plot area zoom and pan events
    const plotArea = this.container.querySelector('#plot-canvas') as SVGSVGElement
    if (plotArea) {
      // Mouse wheel zoom
      plotArea.addEventListener('wheel', (e) => this.handleWheel(e))
      
      // Pan events
      plotArea.addEventListener('mousedown', (e) => this.handlePanStart(e))
      plotArea.addEventListener('mousemove', (e) => this.handlePanMove(e))
      plotArea.addEventListener('mouseup', (e) => this.handlePanEnd(e))
      plotArea.addEventListener('mouseleave', (e) => this.handlePanEnd(e))
      
      // Prevent context menu on right-click for better UX
      plotArea.addEventListener('contextmenu', (e) => e.preventDefault())
      
      // Click to clear selection (but not when dragging labels)
      plotArea.addEventListener('click', (e) => this.handlePlotClick(e))
    }

    // Export functionality
    const exportButton = this.container.querySelector('#export-plot') as HTMLButtonElement
    exportButton?.addEventListener('click', () => {
      this.exportPlot()
    })

    // PDF Export functionality
    const exportPdfButton = this.container.querySelector('#export-pdf') as HTMLButtonElement
    exportPdfButton?.addEventListener('click', () => {
      this.exportPdf()
    })
  }

  private selectCoordinateFromList(index: number): void {
    // Clear any existing highlight timeout
    if (this.highlightTimeout) {
      clearTimeout(this.highlightTimeout)
      this.highlightTimeout = null
    }

    // Toggle selection (click again to deselect)
    const wasSelected = this.selectedCoordinateIndex === index
    this.selectedCoordinateIndex = wasSelected ? null : index
    
    console.log(`🎯 Coordinate ${index + 1} ${wasSelected ? 'deselected' : 'selected'}`)

    // Remove previous line highlights
    const allPaths = this.container.querySelectorAll('.line-segment')
    allPaths.forEach(path => {
      path.setAttribute('stroke', '#000000')
      path.setAttribute('stroke-width', this.plotSettings.lineThickness.toString())
      path.removeAttribute('style')
      ;(path as any).style.filter = 'none'
      ;(path as any).style.transition = ''
    })

    // Re-render to update selection styling and button visibility
    this.render()

    // Highlight corresponding line (only if selecting, not deselecting)
    if (!wasSelected) {
      this.highlightLineFromList(index)
    }
  }

  private highlightLineFromList(index: number): void {
    const lineSegments = this.container.querySelectorAll(`.line-segment`)
    if (lineSegments[index]) {
      // Clear any existing highlight timeout
      if (this.highlightTimeout) {
        clearTimeout(this.highlightTimeout)
        this.highlightTimeout = null
      }

      // Apply highlight
      lineSegments[index].setAttribute('stroke', '#ff6b00')
      lineSegments[index].setAttribute('stroke-width', (this.plotSettings.lineThickness + 2).toString())
      ;(lineSegments[index] as any).style.filter = 'drop-shadow(0px 0px 6px rgba(255, 107, 0, 0.6))'
      
      // Set timeout to fade back to normal after 3 seconds
      this.highlightTimeout = setTimeout(() => {
        const line = lineSegments[index] as SVGElement
        if (line) {
          // Smoothly fade back to normal
          line.style.transition = 'stroke 0.5s ease, stroke-width 0.5s ease, filter 0.5s ease'
          line.setAttribute('stroke', '#000000')
          line.setAttribute('stroke-width', this.plotSettings.lineThickness.toString())
          line.style.filter = 'none'
          
          // Remove transition after animation completes
          setTimeout(() => {
            line.style.transition = ''
          }, 500)
        }
        
        this.highlightTimeout = null
        console.log('🔸 Line highlight faded back to normal')
      }, 3000) // 3 seconds
      
      console.log('🔸 Line highlighted, will fade in 3 seconds')
    }
  }

  private togglePlotLabels(show: boolean): void {
    const labels = this.container.querySelectorAll('.label-group')
    labels.forEach(label => {
      ;(label as HTMLElement).style.display = show ? 'block' : 'none'
    })
  }

  private updateLineThickness(): void {
    // Update the main tract boundary
    const tractBoundary = this.container.querySelector('.tract-boundary')
    if (tractBoundary) {
      tractBoundary.setAttribute('stroke-width', this.plotSettings.lineThickness.toString())
    }

    // Update individual line segments (for highlighting)
    const lines = this.container.querySelectorAll('.line-segment')
    lines.forEach(line => {
      const currentStroke = line.getAttribute('stroke')
      if (!currentStroke || !currentStroke.includes('#ff6b00')) { // Don't update highlighted lines
        line.setAttribute('stroke-width', this.plotSettings.lineThickness.toString())
      }
    })

    // Update endpoint circles to match line thickness
    const circles = this.container.querySelectorAll('.endpoint-circle')
    circles.forEach(circle => {
      circle.setAttribute('stroke-width', this.plotSettings.lineThickness.toString())
    })
  }

  private updateLabelSize(): void {
    // Redraw the entire plot to recalculate label boxes with new font sizes
    this.drawPlot()
    
    console.log('📏 Label size updated to:', this.labelSettings.fontSize)
  }

  private updateHideLabelButton(): void {
    // This method is now handled in the render() method through the template
    // Buttons are shown/hidden based on the selectedCoordinateIndex in the render template
    
    // Update the text content of the visible hide button based on current state
    if (this.selectedCoordinateIndex !== null) {
      const selectedButton = this.container.querySelector(`.hide-label-btn[data-coord-index="${this.selectedCoordinateIndex}"]`) as HTMLButtonElement
      if (selectedButton) {
        const isHidden = this.labelSettings.hiddenLabels.has(this.selectedCoordinateIndex)
        selectedButton.innerHTML = `
          <span class="flex items-center space-x-1">
            <span>${isHidden ? '👁️' : '👁️‍🗨️'}</span>
            <span class="text-xs">${isHidden ? 'Show' : 'Hide'}</span>
          </span>
        `
        selectedButton.title = isHidden ? 'Show coordinate info on plot' : 'Hide coordinate info on plot'
        
        console.log('🔄 Updated hide button for coordinate:', this.selectedCoordinateIndex + 1, 'State:', isHidden ? 'hidden' : 'visible')
      }
    }
  }

  private toggleSelectedLabel(coordinateIndex?: number): void {
    const indexToToggle = coordinateIndex !== undefined ? coordinateIndex : this.selectedCoordinateIndex
    if (indexToToggle === null) return

    // When toggling via button click, ensure the coordinate stays selected
    if (coordinateIndex !== undefined) {
      this.selectedCoordinateIndex = coordinateIndex
    }

    const isHidden = this.labelSettings.hiddenLabels.has(indexToToggle)
    
    if (isHidden) {
      // Show the label
      this.labelSettings.hiddenLabels.delete(indexToToggle)
      this.toastService.info('Info Shown', `Coordinate ${indexToToggle + 1} info is now shown on plot`)
    } else {
      // Hide the label
      this.labelSettings.hiddenLabels.add(indexToToggle)
      this.toastService.info('Info Hidden', `Coordinate ${indexToToggle + 1} now shows just the number`)
    }

    // Update button text (button will stay visible for the selected coordinate)
    this.updateHideLabelButton()
    
    // Save the changes to persist across project switches (async)
    this.debouncedSavePlotSettings()
    
    // Re-render the entire interface to update both the plot AND the green dots in the coordinate list
    this.render()
  }

  private async resetLabelPositions(): Promise<void> {
    try {
      const currentProject = this.appState.getCurrentProject()
      if (!currentProject) {
        this.toastService.error('Reset Failed', 'No active project')
        return
      }

      console.log('🔄 Starting label reset process...')
      
      // Clear custom positions completely
      this.labelSettings.customPositions.clear()
      console.log('🔄 Cleared custom positions from memory, map size now:', this.labelSettings.customPositions.size)
      
      // Reset north arrow position
      this.arrowSettings.customPosition = null
      console.log('🔄 Reset north arrow to default position')
      
      // Force save the empty state first to ensure persistence
      await this.savePlotSettings()
      console.log('✅ Save operation completed')
      
      // Verify the reset by reloading settings from the database
      const verifySettings = await this.apiService.getProjectPlotSettings(currentProject.id)
      if (verifySettings.custom_label_positions === null) {
        console.log('✅ Reset verified: Database shows custom_label_positions = null')
      } else {
        console.warn('⚠️ Reset verification failed: Database still has positions:', verifySettings.custom_label_positions)
      }
      
      // Redraw the plot to restore original positions
      this.drawPlot()
      
      this.toastService.success('Labels Reset', 'All labels moved back to original positions and saved')
      console.log('🔄 Label positions reset and persisted to database')
    } catch (error) {
      console.error('❌ Failed to reset label positions:', error)
      const errorMessage = error instanceof Error ? error.message : String(error)
      this.toastService.error('Reset Failed', `Could not save reset positions: ${errorMessage}`)
    }
      }

  private setupLabelDragging(): void {
    // Set up dragging after a short delay to ensure all elements are rendered
    setTimeout(() => {
      const svg = this.container.querySelector('#plot-canvas') as SVGSVGElement
      if (!svg) return

      // Add mouse event listeners to SVG with direct method references
      svg.addEventListener('mousedown', (e) => this.handleLabelDragStart(e))
      svg.addEventListener('mousemove', (e) => this.handleLabelDragMove(e))
      svg.addEventListener('mouseup', (e) => this.handleLabelDragEnd(e))
      svg.addEventListener('mouseleave', (e) => this.handleLabelDragEnd(e))

      console.log('🖱️ Label dragging setup complete')
    }, 100)
  }

  private handleLabelDragStart(e: MouseEvent): void {
    const target = e.target as SVGElement
    const labelGroup = target.closest('.label-group') as SVGElement
    const arrowGroup = target.closest('.draggable-arrow') as SVGElement
    
    // Handle north arrow dragging
    if (arrowGroup && arrowGroup.getAttribute('data-arrow-id') === 'north-arrow') {
      e.preventDefault()
      e.stopPropagation()
      
      this.dragState.isDragging = true
      this.dragState.draggedLabel = arrowGroup
      
      const svg = this.container.querySelector('#plot-canvas') as SVGSVGElement
      if (!svg) return
      
      // Get SVG point from mouse coordinates
      const rect = svg.getBoundingClientRect()
      const viewBox = svg.viewBox.baseVal
      const scaleX = viewBox.width / rect.width
      const scaleY = viewBox.height / rect.height
      
      const mouseX = (e.clientX - rect.left) * scaleX + viewBox.x
      const mouseY = (e.clientY - rect.top) * scaleY + viewBox.y
      
      // Extract current arrow position from transform
      const transform = arrowGroup.getAttribute('transform') || 'translate(0, 0)'
      let arrowX = 0, arrowY = 0
      
      const match = transform.match(/translate\(([^,]+),([^)]+)\)/)
      if (match) {
        arrowX = parseFloat(match[1])
        arrowY = parseFloat(match[2])
      }
      
      // Calculate offset from mouse to arrow position
      this.dragState.dragOffset = {
        x: mouseX - arrowX,
        y: mouseY - arrowY
      }
      
      this.dragState.originalPosition = { x: arrowX, y: arrowY }
      
      // Visual feedback
      arrowGroup.style.cursor = 'grabbing'
      arrowGroup.style.opacity = '0.8'
      
      console.log('🧭 Started dragging north arrow', 
        `Mouse: (${mouseX.toFixed(1)}, ${mouseY.toFixed(1)}) → Arrow Center: (${arrowX.toFixed(1)}, ${arrowY.toFixed(1)}) → Offset: (${this.dragState.dragOffset.x.toFixed(1)}, ${this.dragState.dragOffset.y.toFixed(1)})`)
      return
    }
    
    // Handle label dragging (existing logic)
    if (labelGroup && (target.classList.contains('bearing-text') || target.classList.contains('distance-text') || target.classList.contains('number-text') || target.tagName === 'rect' || target.tagName === 'circle')) {
      e.preventDefault()
      e.stopPropagation()
      
      this.dragState.isDragging = true
      this.dragState.draggedLabel = labelGroup
      
      const svg = this.container.querySelector('#plot-canvas') as SVGSVGElement
      if (!svg) return
      
      // Get SVG point from mouse coordinates
      const rect = svg.getBoundingClientRect()
      const viewBox = svg.viewBox.baseVal
      const scaleX = viewBox.width / rect.width
      const scaleY = viewBox.height / rect.height
      
      const mouseX = (e.clientX - rect.left) * scaleX + viewBox.x
      const mouseY = (e.clientY - rect.top) * scaleY + viewBox.y
      
      // Since all labels now use transforms, simply extract current position
      const transform = labelGroup.getAttribute('transform') || 'translate(0, 0)'
      let labelX = 0, labelY = 0
      
      const match = transform.match(/translate\(([^,]+),([^)]+)\)/)
      if (match) {
        labelX = parseFloat(match[1])
        labelY = parseFloat(match[2])
      }
      
      // Calculate offset from mouse to label position
      this.dragState.dragOffset = {
        x: mouseX - labelX,
        y: mouseY - labelY
      }
      
      this.dragState.originalPosition = { x: labelX, y: labelY }
      
      // Visual feedback
      labelGroup.style.cursor = 'grabbing'
      labelGroup.style.opacity = '0.8'
      
      console.log('🖱️ Started dragging label', labelGroup.getAttribute('data-label-index'), 
        `Mouse: (${mouseX.toFixed(1)}, ${mouseY.toFixed(1)}) → Label Center: (${labelX.toFixed(1)}, ${labelY.toFixed(1)}) → Offset: (${this.dragState.dragOffset.x.toFixed(1)}, ${this.dragState.dragOffset.y.toFixed(1)})`)
    }
  }

  private handleLabelDragMove(e: MouseEvent): void {
    if (!this.dragState.isDragging || !this.dragState.draggedLabel) return
    
    e.preventDefault()
    
    const svg = this.container.querySelector('#plot-canvas') as SVGSVGElement
    if (!svg) return
    
    // Get SVG point from mouse coordinates
    const rect = svg.getBoundingClientRect()
    const viewBox = svg.viewBox.baseVal
    const scaleX = viewBox.width / rect.width
    const scaleY = viewBox.height / rect.height
    
    const mouseX = (e.clientX - rect.left) * scaleX + viewBox.x
    const mouseY = (e.clientY - rect.top) * scaleY + viewBox.y
    
    // Calculate new label position (mouse position minus the drag offset)
    const newX = mouseX - this.dragState.dragOffset.x
    const newY = mouseY - this.dragState.dragOffset.y
    
    // Apply transform - label should now move smoothly
    this.dragState.draggedLabel.setAttribute('transform', `translate(${newX}, ${newY})`)
  }

  private handleLabelDragEnd(_e: MouseEvent): void {
    if (this.dragState.isDragging && this.dragState.draggedLabel) {
      // Store custom position
      const transform = this.dragState.draggedLabel.getAttribute('transform')
      if (transform) {
        const match = transform.match(/translate\(([^,]+),([^)]+)\)/)
        if (match) {
          const x = parseFloat(match[1])
          const y = parseFloat(match[2])
          
                // Check if this is the north arrow
      if (this.dragState.draggedLabel.getAttribute('data-arrow-id') === 'north-arrow') {
        this.arrowSettings.customPosition = { x, y }
        console.log('🧭 Saved north arrow position:', this.arrowSettings.customPosition)
        
        // Auto-save the new arrow position with error handling
        try {
          this.debouncedSavePlotSettings()
        } catch (error) {
          console.error('❌ Failed to save north arrow position:', error)
          this.toastService.error('Save Failed', 'Could not save north arrow position')
        }
      } else {
            // Handle label dragging (existing logic)
            const labelIndex = parseInt(this.dragState.draggedLabel.getAttribute('data-label-index') || '0')
            this.labelSettings.customPositions.set(labelIndex, { x, y })
            console.log('🖱️ Saved position for label', labelIndex, { x, y })
            
            // Auto-save the new label position
            this.debouncedSavePlotSettings()
          }
        }
      }
      
      // Reset visual feedback
      this.dragState.draggedLabel.style.cursor = 'grab'
      this.dragState.draggedLabel.style.opacity = '1'
      
      // Reset drag state
      const elementType = this.dragState.draggedLabel.getAttribute('data-arrow-id') ? 'north arrow' : 'label'
      this.dragState.isDragging = false
      this.dragState.draggedLabel = null
      
      console.log(`🖱️ Finished dragging ${elementType}`)
    }
  }

  private calculatePolygonArea(points: Array<{ x: number, y: number }>): number {
    if (points.length < 3) return 0
    
    let area = 0
    for (let i = 0; i < points.length; i++) {
      const j = (i + 1) % points.length
      area += points[i].x * points[j].y
      area -= points[j].x * points[i].y
    }
    return Math.abs(area) / 2
  }

  private calculatePerimeter(points: Array<{ x: number, y: number, distance: number, type: string }>): number {
    return points.reduce((sum, point) => sum + point.distance, 0)
  }

  private calculateSurveyingPoints(): Array<{ x: number, y: number, bearing: string, distance: number, originalCoord: string, type: string, curveData?: any }> {
    const points = []
    let currentX = 0
    let currentY = 0
    let currentBearing = 0 // Track the current bearing direction

    console.log('🔍 Processing coordinates for surveying plot:', this.coordinates)

    for (let i = 0; i < this.coordinates.length; i++) {
      const coord = this.coordinates[i]
      const parsed = parseCoordinate(coord)
      
      console.log(`📐 Coordinate ${i + 1}: "${coord}"`, parsed)
      
      if (parsed.type === 'line' && parsed.direction && parsed.degrees !== undefined && parsed.distance !== undefined) {
        // Convert to surveying bearing format
        const bearing = this.convertToSurveyingBearing(parsed.direction, parsed.degrees)
        
        // Calculate end point
        const endPoint = getCoordinatesFromDirection(parsed.direction, parsed.degrees, parsed.distance, currentX, currentY)
        
        // Update current bearing for next curve calculation
        currentBearing = this.calculateBearingFromDirection(parsed.direction, parsed.degrees)
        
        console.log(`📍 Point ${i + 1}: Start(${currentX.toFixed(2)}, ${currentY.toFixed(2)}) → End(${endPoint.x.toFixed(2)}, ${endPoint.y.toFixed(2)}) | Bearing: ${bearing} | Distance: ${parsed.distance}`)
        
        points.push({
          x: endPoint.x,
          y: endPoint.y,
          bearing,
          distance: parsed.distance,
          originalCoord: coord,
          type: 'line'
        })
        
        currentX = endPoint.x
        currentY = endPoint.y
      } else if (parsed.type === 'curve' && parsed.direction && parsed.radius && parsed.delta) {
        console.log(`🌀 Processing curve: ${coord}`)
        
        let endPoint: { x: number, y: number }
        let arcLength: number
        let centerX: number, centerY: number
        
        if (parsed.chordBearing && parsed.chordLength) {
          // Chord-based curve: use chord bearing and length to find end point
          const chordDir = parsed.chordBearing[0] + parsed.chordBearing.slice(-1)
          const chordDegreesStr = parsed.chordBearing.slice(1, -1)
          
          // Parse chord degrees (handle degrees.minutes.seconds format)
          let chordDegrees = 0
          if (chordDegreesStr.includes('.')) {
            const parts = chordDegreesStr.split('.')
            if (parts.length === 3) {
              chordDegrees = parseFloat(parts[0]) + parseFloat(parts[1]) / 60 + parseFloat(parts[2]) / 3600
            } else {
              chordDegrees = parseFloat(chordDegreesStr)
            }
          } else {
            chordDegrees = parseFloat(chordDegreesStr)
          }
          
          endPoint = getCoordinatesFromDirection(chordDir, chordDegrees, parsed.chordLength, currentX, currentY)
          arcLength = parsed.radius * parsed.delta * Math.PI / 180 // Arc length = radius * delta in radians
          
          // Calculate curve center (perpendicular to chord at midpoint)
          const midX = (currentX + endPoint.x) / 2
          const midY = (currentY + endPoint.y) / 2
          const chordBearingRad = this.calculateBearingFromDirection(chordDir, chordDegrees) * Math.PI / 180
          const perpBearing = chordBearingRad + (parsed.direction === 'left' ? Math.PI / 2 : -Math.PI / 2)
          
          // Distance from chord midpoint to center
          const sagitta = parsed.radius - Math.sqrt(parsed.radius * parsed.radius - (parsed.chordLength / 2) * (parsed.chordLength / 2))
          const distToCenter = parsed.radius - sagitta
          
          centerX = midX + distToCenter * Math.cos(perpBearing)
          centerY = midY + distToCenter * Math.sin(perpBearing)
          
        } else {
          // Standard delta-based curve - using v1 logic for center calculation
          const currentBearingRad = currentBearing * Math.PI / 180
          
          // Calculate curve center using v1 approach
          centerX = parsed.direction === 'left'
            ? currentX - parsed.radius * Math.sin(currentBearingRad)
            : currentX + parsed.radius * Math.sin(currentBearingRad)
          centerY = parsed.direction === 'left'
            ? currentY + parsed.radius * Math.cos(currentBearingRad)
            : currentY - parsed.radius * Math.cos(currentBearingRad)
          
          // Calculate end point using v1 approach
          const endBearing = parsed.direction === 'left' ? currentBearing + parsed.delta : currentBearing - parsed.delta
          const endBearingRad = endBearing * Math.PI / 180
          
          // Calculate end point using v1 logic
          endPoint = {
            x: centerX + parsed.radius * Math.sin(endBearingRad),
            y: centerY - parsed.radius * Math.cos(endBearingRad)
          }
          
          arcLength = parsed.radius * parsed.delta * Math.PI / 180 // Arc length = radius * delta in radians
        }
        
        // Update current bearing after the curve
        currentBearing = parsed.direction === 'left' ? currentBearing + parsed.delta : currentBearing - parsed.delta
        
        // Normalize bearing to 0-360° range
        currentBearing = ((currentBearing % 360) + 360) % 360
        
        // Format bearing for display
        const direction = parsed.direction === 'left' ? 'Left' : 'Right'
        const radius = parsed.radius.toFixed(0)
        const bearing = `Curve ${direction} (R=${radius}ft)`
        
        console.log(`🌀 Curve ${i + 1}: Start(${currentX.toFixed(2)}, ${currentY.toFixed(2)}) → End(${endPoint.x.toFixed(2)}, ${endPoint.y.toFixed(2)}) | ${bearing} | Arc: ${arcLength.toFixed(2)}ft`)
        
        points.push({
          x: endPoint.x,
          y: endPoint.y,
          bearing,
          distance: arcLength,
          originalCoord: coord,
          type: 'curve',
          curveData: {
            centerX,
            centerY,
            radius: parsed.radius,
            delta: parsed.delta,
            direction: parsed.direction,
            startAngle: currentBearing - parsed.delta,
            endAngle: currentBearing
          }
        })
        
        currentX = endPoint.x
        currentY = endPoint.y
      } else {
        console.warn(`⚠️ Unrecognized coordinate format: ${coord}`)
      }
    }

    console.log('🎯 Final surveying points:', points)
    return points
  }

  private calculateBearingFromDirection(direction: string, degrees: number): number {
    // Convert surveying bearing to true azimuth (0° = North, clockwise)
    const firstQuadrant = direction[0].toLowerCase() // n or s
    const secondQuadrant = direction[1].toLowerCase() // e or w
    
    if (firstQuadrant === 'n' && secondQuadrant === 'e') {
      return degrees // NE quadrant: 0° to 90°
    } else if (firstQuadrant === 's' && secondQuadrant === 'e') {
      return 180 - degrees // SE quadrant: 90° to 180°
    } else if (firstQuadrant === 's' && secondQuadrant === 'w') {
      return 180 + degrees // SW quadrant: 180° to 270°
    } else if (firstQuadrant === 'n' && secondQuadrant === 'w') {
      return 360 - degrees // NW quadrant: 270° to 360°
    }
    return 0
  }

  private convertToSurveyingBearing(direction: string, degrees: number): string {
    // Convert to proper surveying bearing format like "N89°43'55"E"
    const NS = direction[0].toUpperCase()
    const EW = direction[1].toUpperCase()
    
    const deg = Math.floor(degrees)
    const min = Math.floor((degrees - deg) * 60)
    const sec = Math.floor(((degrees - deg) * 60 - min) * 60)
    
    return `${NS}${deg}°${min.toString().padStart(2, '0')}'${sec.toString().padStart(2, '0')}"${EW}`
  }

  private calculateBounds(points: Array<{ x: number, y: number }>): { minX: number, minY: number, maxX: number, maxY: number, width: number, height: number } {
    if (points.length === 0) return { minX: 0, minY: 0, maxX: 100, maxY: 100, width: 100, height: 100 }
    
    let minX = points[0].x
    let maxX = points[0].x
    let minY = -points[0].y // Flip Y for SVG bounds calculation
    let maxY = -points[0].y
    
    // Include the starting point (0,0)
    minX = Math.min(minX, 0)
    maxX = Math.max(maxX, 0)
    minY = Math.min(minY, 0)
    maxY = Math.max(maxY, 0)
    
    points.forEach(point => {
      minX = Math.min(minX, point.x)
      maxX = Math.max(maxX, point.x)
      minY = Math.min(minY, -point.y) // Flip Y for SVG coordinate system
      maxY = Math.max(maxY, -point.y)
    })
    
    return {
      minX,
      minY,
      maxX,
      maxY,
      width: maxX - minX,
      height: maxY - minY
    }
  }

  private drawPlot(): void {
    const svg = this.container.querySelector('#plot-canvas') as SVGSVGElement
    if (!svg) {
      console.error('❌ SVG canvas not found')
      return
    }

    // Clear existing content
    svg.innerHTML = ''
    
    const points = this.calculateSurveyingPoints()
    if (points.length === 0) {
      console.warn('⚠️ No points to draw')
      return
    }
    
    const bounds = this.calculateBounds(points)
    const padding = 50

    // Create main content group for zoom/pan transforms
    const contentGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g')
    contentGroup.setAttribute('class', 'plot-content')
    svg.appendChild(contentGroup)
    
    // Apply initial transform to ensure proper state
    this.updateTransform()
    
    // Create the complete boundary path with proper curves
    let pathData = `M 0 0` // Start at origin
    let currentX = 0
    let currentY = 0
    
    points.forEach((point) => {
      if (point.type === 'line') {
        pathData += ` L ${point.x} ${-point.y}` // Flip Y for SVG coordinate system
      } else if (point.type === 'curve' && point.curveData) {
        // Draw SVG arc for curves
        const curve = point.curveData
        const largeArcFlag = curve.delta > 180 ? 1 : 0
        // Use v1 sweep flag logic (which was working)
        const sweepFlag = curve.direction === 'left' ? 0 : 1
        
        pathData += ` A ${curve.radius} ${curve.radius} 0 ${largeArcFlag} ${sweepFlag} ${point.x} ${-point.y}`
      }
      currentX = point.x
      currentY = point.y
    })
     
    // Never close the loop - always show as open traverse
    // pathData does not include Z command
     
    // Create the main path with proper curves (no fill)
    const polygon = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    polygon.setAttribute('d', pathData)
    // Never show fill - always show as stroke only
    polygon.setAttribute('fill', 'none')
    polygon.setAttribute('stroke', '#000000')
    polygon.setAttribute('stroke-width', this.plotSettings.lineThickness.toString())
    polygon.setAttribute('class', 'tract-boundary')
    contentGroup.appendChild(polygon)
     
    // First: Add circles at each segment endpoint (behind everything else)
    
    // Add circle at starting point (origin)
    const startCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
    startCircle.setAttribute('cx', '0')
    startCircle.setAttribute('cy', '0')
    startCircle.setAttribute('r', '4')
    startCircle.setAttribute('fill', 'none')
    startCircle.setAttribute('stroke', '#000000')
    startCircle.setAttribute('stroke-width', this.plotSettings.lineThickness.toString())
    startCircle.setAttribute('class', 'endpoint-circle')
    contentGroup.appendChild(startCircle)
    
    points.forEach((point) => {
      // Add circle at end of each segment
      const endCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
      endCircle.setAttribute('cx', point.x.toString())
      endCircle.setAttribute('cy', (-point.y).toString()) // Flip Y for SVG coordinate system
      endCircle.setAttribute('r', '4')
      endCircle.setAttribute('fill', 'none')
      endCircle.setAttribute('stroke', '#000000')
      endCircle.setAttribute('stroke-width', this.plotSettings.lineThickness.toString())
      endCircle.setAttribute('class', 'endpoint-circle')
      contentGroup.appendChild(endCircle)
    })

    // Second: Draw individual segments for highlighting and labels (on top of circles)
    currentX = 0
    currentY = 0
     
    points.forEach((point, index) => {
      if (point.type === 'line') {
        // Create line segment for highlighting
        const line = document.createElementNS('http://www.w3.org/2000/svg', 'line')
        line.setAttribute('x1', currentX.toString())
        line.setAttribute('y1', (-currentY).toString()) 
        line.setAttribute('x2', point.x.toString())
        line.setAttribute('y2', (-point.y).toString())
        line.setAttribute('stroke', 'transparent') // Invisible but clickable
        line.setAttribute('stroke-width', '8')
        line.setAttribute('class', `line-segment line-${index}`)
        line.style.cursor = 'pointer'
        contentGroup.appendChild(line)
      } else if (point.type === 'curve' && point.curveData) {
        // Create arc path for curve highlighting
        const curve = point.curveData
        const largeArcFlag = curve.delta > 180 ? 1 : 0
        // Use v1 sweep flag logic (which was working)
        const sweepFlag = curve.direction === 'left' ? 0 : 1
        
        const arcPath = document.createElementNS('http://www.w3.org/2000/svg', 'path')
        const arcPathData = `M ${currentX} ${-currentY} A ${curve.radius} ${curve.radius} 0 ${largeArcFlag} ${sweepFlag} ${point.x} ${-point.y}`
        arcPath.setAttribute('d', arcPathData)
        arcPath.setAttribute('stroke', 'transparent') // Invisible but clickable
        arcPath.setAttribute('stroke-width', '12') // Slightly wider for curves
        arcPath.setAttribute('fill', 'none')
        arcPath.setAttribute('class', `line-segment line-${index}`)
        arcPath.style.cursor = 'pointer'
        contentGroup.appendChild(arcPath)
      }
       
      // Always create labels, then apply visibility based on settings
      this.drawLabels(contentGroup, currentX, currentY, point.x, point.y, point.bearing, point.distance, index)
       
      currentX = point.x
      currentY = point.y
    })
    
    // Add north arrow
    this.drawNorthArrow(contentGroup, bounds, padding)
     
    // Apply initial label visibility based on current settings
    this.togglePlotLabels(this.plotSettings.showLabels)
    
    // Set up label dragging after all elements are rendered
    this.setupLabelDragging()
     
    console.log('🎨 Plot drawn successfully with', points.length, 'segments (including curves)')
  }



  private drawLabels(parent: SVGSVGElement | SVGGElement, startX: number, startY: number, endX: number, endY: number, bearing: string, distance: number, index: number): void {
    const midX = (startX + endX) / 2
    const midY = (startY + endY) / 2
    
    // Calculate the visual center position (labels will be positioned around this point)
    const centerX = midX
    const centerY = -midY // Flip Y for SVG coordinate system
    
    // Check if we have custom position for this label
    const customPos = this.labelSettings.customPositions.get(index)
    const finalX = customPos ? customPos.x : centerX
    const finalY = customPos ? customPos.y : centerY
    
    // Create label group - ALWAYS use transform positioning for consistency
    const labelGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g')
    labelGroup.setAttribute('class', 'label-group')
    labelGroup.setAttribute('data-label-index', index.toString())
    labelGroup.style.cursor = 'grab'
    labelGroup.setAttribute('title', 'Click to highlight coordinate, drag to move label')
    labelGroup.setAttribute('transform', `translate(${finalX}, ${finalY})`)
    
    // Add click event listener for reverse highlighting (label click -> coordinate highlight)
    labelGroup.addEventListener('click', (e) => {
      // Prevent event if we're dragging
      if (this.dragState.isDragging) {
        return
      }
      
      e.stopPropagation()
      console.log('🔸 Label clicked, highlighting coordinate', index + 1)
      this.selectCoordinateFromList(index)
    })
    
    // Check if this label should be hidden (show only number)
    const isHidden = this.labelSettings.hiddenLabels.has(index)
    
    if (isHidden) {
      // Show only the coordinate number - very small and perfectly centered
      const numberText = document.createElementNS('http://www.w3.org/2000/svg', 'text')
      numberText.setAttribute('x', '0')
      numberText.setAttribute('y', '0') // Centered
      numberText.setAttribute('text-anchor', 'middle')
      numberText.setAttribute('dominant-baseline', 'central') // Perfect vertical centering
      numberText.setAttribute('font-size', '10') // Even smaller size
      numberText.setAttribute('font-weight', 'bold')
      numberText.setAttribute('fill', '#1f2937')
      numberText.setAttribute('class', 'number-text')
      numberText.textContent = (index + 1).toString()
      
      labelGroup.appendChild(numberText)
      parent.appendChild(labelGroup)
      
      // Add simple background circle for the number - very compact
      try {
        const bbox = numberText.getBBox()
        const radius = Math.max(bbox.width, bbox.height) / 2 + 2 // Minimal padding
        
        const bgCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
        bgCircle.setAttribute('cx', '0')
        bgCircle.setAttribute('cy', '0')
        bgCircle.setAttribute('r', radius.toString())
        bgCircle.setAttribute('fill', 'rgba(255, 255, 255, 0.90)')
        bgCircle.setAttribute('stroke', '#3b82f6')
        bgCircle.setAttribute('stroke-width', '1.5')
        
        // Insert background before text
        labelGroup.insertBefore(bgCircle, numberText)
      } catch (error) {
        console.warn('Could not create number background:', error)
      }
      
      return // Exit early for hidden labels
    }
    
    // Calculate proportional spacing - balanced between old fixed spacing and font scaling
    const baseSpacing = Math.max(this.labelSettings.fontSize * 1.5, 16) // Minimum 16px, scales with font
    const bearingOffset = -baseSpacing / 2
    const distanceOffset = baseSpacing / 2
    
    // Bearing label (positioned relative to group center)
    const bearingText = document.createElementNS('http://www.w3.org/2000/svg', 'text')
    bearingText.setAttribute('x', '0')
    bearingText.setAttribute('y', bearingOffset.toString()) // Above center, scaled
    bearingText.setAttribute('text-anchor', 'middle')
    bearingText.setAttribute('font-size', this.labelSettings.fontSize.toString())
    bearingText.setAttribute('font-weight', 'bold')
    bearingText.setAttribute('fill', '#1f2937')
    bearingText.setAttribute('class', 'bearing-text')
    bearingText.textContent = bearing
    
    // Distance label (positioned relative to group center)
    const distanceText = document.createElementNS('http://www.w3.org/2000/svg', 'text')
    distanceText.setAttribute('x', '0')
    distanceText.setAttribute('y', distanceOffset.toString()) // Below center, scaled
    distanceText.setAttribute('text-anchor', 'middle')
    distanceText.setAttribute('font-size', (this.labelSettings.fontSize - 2).toString())
    distanceText.setAttribute('fill', '#6b7280')
    distanceText.setAttribute('class', 'distance-text')
    distanceText.textContent = `${distance.toFixed(2)}ft`
    
    // Add text elements to DOM first so we can get their dimensions
    labelGroup.appendChild(bearingText)
    labelGroup.appendChild(distanceText)
    parent.appendChild(labelGroup)
    
    // Now get bounding boxes for background rectangle
    try {
      const bbox1 = bearingText.getBBox()
      const bbox2 = distanceText.getBBox()
      const maxWidth = Math.max(bbox1.width, bbox2.width)
      
      // Background rectangle - calculate precise bounds to contain all text
      const textPadding = 4
      const rectTop = bearingOffset - this.labelSettings.fontSize / 2 - textPadding * 1.8 // Much more generous padding above
      const rectBottom = distanceOffset + (this.labelSettings.fontSize - 2) / 2 + textPadding * 0.7 // Less padding below
      const rectHeight = rectBottom - rectTop
      
      const bgRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
      bgRect.setAttribute('x', (-maxWidth / 2 - textPadding).toString())
      bgRect.setAttribute('y', rectTop.toString())
      bgRect.setAttribute('width', (maxWidth + 2 * textPadding).toString())
      bgRect.setAttribute('height', rectHeight.toString())
      bgRect.setAttribute('fill', 'rgba(255, 255, 255, 0.75)')
      bgRect.setAttribute('stroke', '#e5e7eb')
      bgRect.setAttribute('stroke-width', '1')
      bgRect.setAttribute('rx', '4')
      
      // Insert background rectangle before text elements
      labelGroup.insertBefore(bgRect, bearingText)
    } catch (error) {
      console.warn('Could not create label background:', error)
      // Continue without background rectangle
    }
  }

  private drawNorthArrow(parent: SVGSVGElement | SVGGElement, bounds: any, padding: number): void {
    // Determine arrow position - use custom position if available, otherwise default
    let arrowX = bounds.maxX + padding - 30
    let arrowY = bounds.minY + padding + 30
    
    if (this.arrowSettings.customPosition) {
      arrowX = this.arrowSettings.customPosition.x
      arrowY = this.arrowSettings.customPosition.y
      console.log('🧭 Using custom north arrow position:', this.arrowSettings.customPosition)
    } else {
      console.log('🧭 Using default north arrow position:', { x: arrowX, y: arrowY })
    }
    
    const arrowGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g')
    arrowGroup.setAttribute('class', 'north-arrow draggable-arrow')
    arrowGroup.setAttribute('data-arrow-id', 'north-arrow')
    arrowGroup.setAttribute('transform', `translate(${arrowX}, ${arrowY})`)
    arrowGroup.style.cursor = 'grab'
    
    // Simple invisible background circle for drag area - enlarged for longer arrow
    const background = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
    background.setAttribute('cx', '0')
    background.setAttribute('cy', '0')
    background.setAttribute('r', '25')
    background.setAttribute('fill', 'transparent')
    background.setAttribute('stroke', 'transparent')
    background.setAttribute('class', 'arrow-background')
    
    // Simple arrow shaft (line) - made longer and stops before arrow head
    const shaft = document.createElementNS('http://www.w3.org/2000/svg', 'line')
    shaft.setAttribute('x1', '0')
    shaft.setAttribute('y1', '12')
    shaft.setAttribute('x2', '0')
    shaft.setAttribute('y2', '-6')
    shaft.setAttribute('stroke', '#374151')
    shaft.setAttribute('stroke-width', '2')
    shaft.setAttribute('stroke-linecap', 'round')
    shaft.setAttribute('class', 'arrow-shaft')
    
    // Simple arrow head (triangle) - positioned to not overlap with shaft
    const head = document.createElementNS('http://www.w3.org/2000/svg', 'polygon')
    head.setAttribute('points', '0,-12 -5,-4 5,-4')
    head.setAttribute('fill', '#374151')
    head.setAttribute('class', 'arrow-head')
    
    // "N" label - positioned above the longer arrow
    const nLabel = document.createElementNS('http://www.w3.org/2000/svg', 'text')
    nLabel.setAttribute('x', '0')
    nLabel.setAttribute('y', '-17')
    nLabel.setAttribute('text-anchor', 'middle')
    nLabel.setAttribute('font-size', '12')
    nLabel.setAttribute('font-weight', 'bold')
    nLabel.setAttribute('fill', '#374151')
    nLabel.setAttribute('class', 'arrow-label')
    nLabel.textContent = 'N'
    
    // Hover effects - simple highlighting
    arrowGroup.addEventListener('mouseenter', () => {
      shaft.setAttribute('stroke', '#1f2937')
      head.setAttribute('fill', '#1f2937')
      nLabel.setAttribute('fill', '#1f2937')
      arrowGroup.style.cursor = 'grab'
    })
    
    arrowGroup.addEventListener('mouseleave', () => {
      if (!this.dragState.isDragging || this.dragState.draggedLabel !== arrowGroup) {
        shaft.setAttribute('stroke', '#374151')
        head.setAttribute('fill', '#374151')
        nLabel.setAttribute('fill', '#374151')
        arrowGroup.style.cursor = 'grab'
      }
    })
    
    // Assembly order
    arrowGroup.appendChild(background)
    arrowGroup.appendChild(shaft)
    arrowGroup.appendChild(head)
    arrowGroup.appendChild(nLabel)
    
    parent.appendChild(arrowGroup)
  }

  // Zoom and Pan Methods
  private zoomIn(): void {
    const newScale = Math.min(this.viewState.scale * 1.5, this.viewState.maxScale)
    this.setZoomToCenter(newScale)
    // Save zoom state after zoom change
    this.debouncedSavePlotSettings()
  }

  private zoomOut(): void {
    const newScale = Math.max(this.viewState.scale / 1.5, this.viewState.minScale)
    this.setZoomToCenter(newScale)
    // Save zoom state after zoom change
    this.debouncedSavePlotSettings()
  }

  private resetView(): void {
    this.viewState.scale = 1
    this.viewState.translateX = 0
    this.viewState.translateY = 0
    this.updateTransform()
    this.updateZoomDisplay()
    // Save zoom state after reset
    this.debouncedSavePlotSettings()
  }

  private setZoomToCenter(newScale: number): void {
    const svg = this.container.querySelector('#plot-canvas') as SVGSVGElement
    const plotContent = svg?.querySelector('.plot-content') as SVGGElement
    
    if (!svg || !plotContent) {
      console.error('❌ SVG or plot content not found for zoom')
      return
    }
    
    const clampedScale = Math.max(this.viewState.minScale, Math.min(newScale, this.viewState.maxScale))
    
    // Get the SVG viewBox to understand the coordinate system
    const viewBox = svg.getAttribute('viewBox')
    if (!viewBox) {
      console.error('❌ SVG viewBox not found')
      return
    }
    
    const [viewBoxX, viewBoxY, viewBoxWidth, viewBoxHeight] = viewBox.split(' ').map(Number)
    
    // The center point in viewBox coordinates (this is our zoom focal point)
    const centerX = viewBoxX + viewBoxWidth / 2
    const centerY = viewBoxY + viewBoxHeight / 2
    
    // Calculate what point in the plot coordinate system is currently at the center
    const currentPlotX = (centerX - this.viewState.translateX) / this.viewState.scale
    const currentPlotY = (centerY - this.viewState.translateY) / this.viewState.scale
    
    // Update the scale
    this.viewState.scale = clampedScale
    
    // Calculate new translation to keep the same plot point at the center
    this.viewState.translateX = centerX - currentPlotX * this.viewState.scale
    this.viewState.translateY = centerY - currentPlotY * this.viewState.scale
    
    this.updateTransform()
    this.updateZoomDisplay()
    
    console.log('🔍 Zoom to center:', {
      scale: `${Math.round(this.viewState.scale * 100)}%`,
      translate: `(${this.viewState.translateX.toFixed(1)}, ${this.viewState.translateY.toFixed(1)})`,
      viewBoxCenter: `(${centerX.toFixed(1)}, ${centerY.toFixed(1)})`,
      plotPointAtCenter: `(${currentPlotX.toFixed(1)}, ${currentPlotY.toFixed(1)})`
    })
  }

  private updateTransform(): void {
    const svg = this.container.querySelector('#plot-canvas') as SVGSVGElement
    if (!svg) return

    const group = svg.querySelector('.plot-content') as SVGGElement
    if (group) {
      group.setAttribute('transform', 
        `translate(${this.viewState.translateX}, ${this.viewState.translateY}) scale(${this.viewState.scale})`
      )
    }
  }

  private updateZoomDisplay(): void {
    const zoomLevel = `${Math.round(this.viewState.scale * 100)}%`
    
    const zoomDisplay = this.container.querySelector('#zoom-level')
    if (zoomDisplay) {
      zoomDisplay.textContent = zoomLevel
    }
    
    const zoomDisplayMini = this.container.querySelector('#zoom-level-mini')
    if (zoomDisplayMini) {
      zoomDisplayMini.textContent = zoomLevel
    }
  }

  private handleWheel(e: WheelEvent): void {
    e.preventDefault()
    
    const delta = e.deltaY > 0 ? 0.9 : 1.1
    const newScale = Math.max(this.viewState.minScale, Math.min(this.viewState.scale * delta, this.viewState.maxScale))
    
    // Zoom towards mouse position
    const svg = e.currentTarget as SVGSVGElement
    const rect = svg.getBoundingClientRect()
    
    // Convert mouse screen coordinates to SVG viewBox coordinates
    const mouseScreenX = e.clientX - rect.left
    const mouseScreenY = e.clientY - rect.top
    
    // Get SVG viewBox parameters
    const viewBox = svg.getAttribute('viewBox')
    if (!viewBox) {
      console.error('❌ SVG viewBox not found for zoom')
      return
    }
    
    const [viewBoxX, viewBoxY, viewBoxWidth, viewBoxHeight] = viewBox.split(' ').map(Number)
    
    // Convert screen coordinates to SVG viewBox coordinates
    const svgX = viewBoxX + (mouseScreenX / rect.width) * viewBoxWidth
    const svgY = viewBoxY + (mouseScreenY / rect.height) * viewBoxHeight
    
    // Calculate the point in the plot coordinate system that's under the mouse
    // This accounts for the current transform
    const plotX = (svgX - this.viewState.translateX) / this.viewState.scale
    const plotY = (svgY - this.viewState.translateY) / this.viewState.scale
    
    // Update scale
    this.viewState.scale = newScale
    
    // Adjust translation to keep the plot point under the mouse cursor
    this.viewState.translateX = svgX - plotX * this.viewState.scale
    this.viewState.translateY = svgY - plotY * this.viewState.scale
    
    this.updateTransform()
    this.updateZoomDisplay()
    
    // Throttled save for wheel zoom to avoid too many API calls
    this.throttledZoomSave()
  }
  
  private throttledZoomSave(): void {
    // Clear any existing timeout
    if (this.zoomSaveTimeout) {
      clearTimeout(this.zoomSaveTimeout)
    }
    
    // Set a new timeout to save after 1 second of inactivity
    this.zoomSaveTimeout = setTimeout(() => {
      this.debouncedSavePlotSettings()
    }, 1000)
  }

  private handlePanStart(e: MouseEvent): void {
    // Only pan with left mouse button and not when dragging labels
    if (e.button !== 0 || this.dragState.isDragging) return
    
    const target = e.target as Element
    // Don't start panning if clicking on a label, arrow, or interactive element
    if (target.closest('.label-group') || target.closest('.draggable-arrow') || target.tagName === 'text') return
    
    this.panState.isPanning = true
    this.panState.startX = e.clientX
    this.panState.startY = e.clientY
    this.panState.startTranslateX = this.viewState.translateX
    this.panState.startTranslateY = this.viewState.translateY
    
    const svg = e.currentTarget as SVGSVGElement
    svg.style.cursor = 'grabbing'
  }

  private handlePanMove(e: MouseEvent): void {
    if (!this.panState.isPanning) return
    
    e.preventDefault()
    
    const deltaX = e.clientX - this.panState.startX
    const deltaY = e.clientY - this.panState.startY
    
    this.viewState.translateX = this.panState.startTranslateX + deltaX
    this.viewState.translateY = this.panState.startTranslateY + deltaY
    
    this.updateTransform()
  }

  private handlePanEnd(e: MouseEvent): void {
    if (this.panState.isPanning) {
      this.panState.isPanning = false
      
      const svg = e.currentTarget as SVGSVGElement
      svg.style.cursor = 'default'
      
      // Save zoom state after panning
      this.debouncedSavePlotSettings()
    }
  }

  private handlePlotClick(e: MouseEvent): void {
    // Don't clear selection if we're dragging a label/arrow or clicking on a label/arrow
    const target = e.target as SVGElement
    if (this.dragState.isDragging || target.closest('.label-group') || target.closest('.draggable-arrow')) {
      return
    }

    // Clear coordinate selection
    this.selectedCoordinateIndex = null
    this.updateHideLabelButton()
    
    // Remove selection styling from coordinate list
    const coordinateItems = this.container.querySelectorAll('.coordinate-item')
    coordinateItems.forEach(item => {
      item.classList.remove('selected', 'bg-blue-100')
      item.classList.add('hover:bg-blue-50')
    })
  }

  private exportPlot(): void {
    const svg = this.container.querySelector('#plot-canvas') as SVGSVGElement
    if (!svg) {
      console.error('❌ SVG canvas not found for export')
      return
    }

    try {
      // Clone the SVG to avoid modifying the original
      const svgClone = svg.cloneNode(true) as SVGSVGElement
      
      // Add XML namespace declaration
      svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
      
      // Get the SVG as string
      const svgString = new XMLSerializer().serializeToString(svgClone)
      
      // Create and download the file
      const blob = new Blob([svgString], { type: 'image/svg+xml' })
      const url = URL.createObjectURL(blob)
      
      const link = document.createElement('a')
      link.href = url
      link.download = `tract-plot-${new Date().toISOString().slice(0, 10)}.svg`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      URL.revokeObjectURL(url)
      
      this.toastService.success('Plot exported successfully', 'SVG file downloaded')
      console.log('📁 Plot exported as SVG')
    } catch (error) {
      console.error('Failed to export plot:', error)
      this.toastService.error('Export failed', 'Unable to export plot as SVG')
    }
  }

  private exportPdf(): void {
    try {
      const svg = this.container.querySelector('#plot-canvas') as SVGSVGElement
      if (!svg) {
        console.error('❌ SVG canvas not found for PDF export')
        return
      }

      // Get tract information
      const points = this.calculateSurveyingPoints()
      
      // Get project name for title
      const currentProject = this.appState.getCurrentProject()
      const projectName = currentProject ? currentProject.name : 'Survey Plot'

      // Clone and prepare SVG for PDF export
      const svgClone = svg.cloneNode(true) as SVGSVGElement
      
      // Reset any zoom/pan transforms for PDF export
      const plotContent = svgClone.querySelector('.plot-content') as SVGGElement
      if (plotContent) {
        plotContent.removeAttribute('transform')
      }
      
      // Calculate proper bounds for the plot
      const bounds = this.calculateBounds(points)
      const padding = 100  // More padding for PDF
      
      // Set explicit dimensions for print - maximize plot area with metes & bounds at bottom
      const svgWidth = 750  // Increased width to use more page space
      const svgHeight = 580 // Increased height to maximize plot area
      
      // Calculate content dimensions
      const contentWidth = bounds.width + 2 * padding
      const contentHeight = bounds.height + 2 * padding
      
      // Apply proper viewBox and dimensions
      svgClone.setAttribute('width', svgWidth.toString())
      svgClone.setAttribute('height', svgHeight.toString())
      svgClone.setAttribute('viewBox', 
        `${bounds.minX - padding} ${bounds.minY - padding} ${contentWidth} ${contentHeight}`
      )
      svgClone.style.maxWidth = '100%'
      svgClone.style.height = 'auto'

      // Format current date and time professionally
      const now = new Date()
      const dateOptions: Intl.DateTimeFormatOptions = { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric'
      }
      const timeOptions: Intl.DateTimeFormatOptions = { 
        hour: 'numeric', 
        minute: '2-digit', 
        hour12: true 
      }
      const formattedDate = now.toLocaleDateString('en-US', dateOptions)
      const formattedTime = now.toLocaleTimeString('en-US', timeOptions)

             // Format metes and bounds with better structure
       const metesAndBoundsFormatted = this.coordinates.map((coord, index) => {
         // Parse the coordinate to get structured data for better formatting
         try {
           const parsed = parseCoordinate(coord)
           if (parsed && parsed.type === 'line' && parsed.direction && parsed.distance) {
             const bearing = `${parsed.direction.toUpperCase()} ${parsed.degrees?.toFixed(2)}°`
             return `${index + 1}. ${bearing}, ${parsed.distance.toFixed(2)} feet`
           } else if (parsed && parsed.type === 'curve' && parsed.direction && parsed.radius) {
             return `${index + 1}. Curve ${parsed.direction}, radius ${parsed.radius.toFixed(2)} feet, delta ${parsed.delta?.toFixed(2)}°`
           }
         } catch (e) {
           // Fallback to original format if parsing fails
         }
         return `${index + 1}. ${coord}`
       })

      // Group coordinates into columns for better readability
      const coordsPerColumn = Math.ceil(metesAndBoundsFormatted.length / 2)
      const leftColumn = metesAndBoundsFormatted.slice(0, coordsPerColumn)
      const rightColumn = metesAndBoundsFormatted.slice(coordsPerColumn)

      // Create HTML content for PDF with enhanced layout
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${projectName} - Survey Plot</title>
          <meta charset="UTF-8">
          <style>
            @page { 
              size: letter; 
              margin: 0.4in; 
            }
            body { 
              font-family: 'Times New Roman', serif; 
              margin: 0; 
              padding: 0; 
              font-size: 11px;
              line-height: 1.3;
              color: #000;
              display: flex;
              flex-direction: column;
              height: 100vh;
            }
            .header { 
              text-align: center; 
              margin-bottom: 8px; 
              flex-shrink: 0;
              border-bottom: 1px solid #333;
              padding-bottom: 6px;
            }
            .header h1 { 
              margin: 0 0 8px 0; 
              font-size: 20px; 
              font-weight: bold;
              color: #000;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }

            .plot-container { 
              flex: 1;
              text-align: center;
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 0;
              margin: 5px 0;
            }
            .footer {
              flex-shrink: 0;
              margin-top: 8px;
              border-top: 1px solid #333;
              padding-top: 6px;
            }
            .tract-info {
              display: grid;
              grid-template-columns: 1fr 1fr 1fr;
              gap: 10px;
              margin-bottom: 10px;
              font-size: 9px;
              text-align: center;
            }
            .tract-stat {
              padding: 5px;
              background: #f8f8f8;
              border: 1px solid #ddd;
              border-radius: 3px;
            }
            .tract-stat .label {
              font-weight: bold;
              color: #333;
              display: block;
              margin-bottom: 2px;
            }
            .tract-stat .value {
              font-size: 10px;
              color: #000;
            }
            .metes-bounds {
              font-size: 8px;
              line-height: 1.4;
            }
            .metes-bounds h3 {
              margin: 0 0 8px 0;
              font-size: 10px;
              font-weight: bold;
              text-align: center;
              color: #333;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            .coordinates-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 10px;
              text-align: left;
            }
            .coordinate-column {
              font-size: 7px;
              line-height: 1.5;
            }
            .coordinate-item {
              margin-bottom: 3px;
              padding: 2px 0;
            }
            .coordinate-number {
              font-weight: bold;
              color: #000;
            }
            .generation-info {
              text-align: center;
              margin-top: 6px;
              padding-top: 4px;
              border-top: 1px solid #ddd;
              font-size: 7px;
              color: #888;
              font-style: italic;
            }
            svg { 
              background: white;
              max-width: 100%;
              max-height: 100%;
              width: auto;
              height: auto;
            }
            
            /* Print-specific styles */
            @media print {
              body { 
                height: 100vh;
                font-size: 10px;
              }
              .header { 
                border-bottom: none;
                margin-bottom: 4px;
                padding-bottom: 2px;
              }
              .header h1 { 
                font-size: 16px; 
              }
              .tract-info { 
                font-size: 8px;
              }
              .metes-bounds { 
                font-size: 7px;
              }

              .generation-info {
                font-size: 6px;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${projectName}</h1>
          </div>
          
          <div class="plot-container">
            ${svgClone.outerHTML}
          </div>
          
          <div class="footer">            
            <div class="metes-bounds">
              <div class="coordinates-grid">
                <div class="coordinate-column">
                  ${leftColumn.map(coord => `<div class="coordinate-item">${coord}</div>`).join('')}
                </div>
                <div class="coordinate-column">
                  ${rightColumn.map(coord => `<div class="coordinate-item">${coord}</div>`).join('')}
                </div>
              </div>
            </div>
            
            <div class="generation-info">
              Document generated by Doc2Plot Survey Platform • ${formattedDate} ${formattedTime}
            </div>
          </div>
        </body>
        </html>
      `

      // Create a new window for printing
      const printWindow = window.open('', '_blank')
      if (printWindow) {
        printWindow.document.write(htmlContent)
        printWindow.document.close()
        
        // Wait for content to load, then print
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print()
            printWindow.close()
          }, 750) // Slightly longer delay for complex layout
        }
        
        this.toastService.success('Optimized PDF Export Ready', 'Maximized plot area with clean, borderless layout for professional presentation')
        console.log('📄 Optimized PDF export ready - maximized plot area with clean layout')
      } else {
        throw new Error('Unable to open print window')
      }
    } catch (error) {
      console.error('Failed to export PDF:', error)
      this.toastService.error('PDF Export Failed', 'Unable to generate PDF. Please try again.')
    }
  }



  public refresh(): void {
    try {
      console.log('🔄 PlotViewer refresh: reloading coordinates and updating UI')
      
      // Clear any existing highlight timeout
      if (this.highlightTimeout) {
        clearTimeout(this.highlightTimeout)
        this.highlightTimeout = null
      }
      
      this.loadCoordinates()
      
      // Re-render the component to ensure all elements exist
      this.render()
      
      // Update coordinate list and button states
      this.updateConvertButton()
      
      // If we have coordinates, update the plot
      if (this.coordinates.length > 0) {
        this.render()
      }
      
      // Ensure label visibility is properly applied after refresh
      setTimeout(() => {
        this.applyLabelVisibility()
      }, 100)
      
      console.log('✅ PlotViewer refresh completed successfully')
    } catch (error) {
      console.error('❌ PlotViewer refresh failed:', error)
      this.toastService.error('Plot Refresh Failed', 'Unable to refresh plotting interface')
    }
  }

  // IMPROVED UI: Add coordinate editing methods for left panel
  private toggleCoordinateEdit(index: number): void {
    const coordinateItem = this.container.querySelector(`[data-index="${index}"].coordinate-item`)
    const editButton = coordinateItem?.querySelector('.edit-coordinate-btn') as HTMLButtonElement
    const editInput = coordinateItem?.querySelector('.coordinate-edit-input') as HTMLInputElement
    const displayText = coordinateItem?.querySelector('.font-medium')
    const distanceText = coordinateItem?.querySelector('.text-sm')

    if (!editButton || !editInput || !displayText || !distanceText) return

    const isEditing = editInput.classList.contains('hidden') === false

    console.log('🔄 toggleCoordinateEdit called for index:', index, 'isEditing:', isEditing)

    if (isEditing) {
      // Save current edit
      console.log('💾 Saving edit for index:', index, 'value:', editInput.value)
      this.saveCoordinateEdit(index, editInput.value)
    } else {
      // Enter edit mode
      console.log('✏️ Entering edit mode for index:', index)
      this.activeEditingIndex = index // Set active editing flag
      
      editInput.classList.remove('hidden')
      displayText.classList.add('hidden')
      distanceText.classList.add('hidden')
      
      // Update button styling for save mode
      editButton.innerHTML = `
        <span class="flex items-center space-x-1">
          <span>💾</span>
          <span class="font-medium">Save</span>
        </span>
      `
      editButton.classList.remove('bg-emerald-500', 'hover:bg-emerald-600')
      editButton.classList.add('bg-blue-500', 'hover:bg-blue-600')
      editButton.title = 'Save changes'
      
      // Focus with a slight delay to ensure DOM is updated
      setTimeout(() => {
        editInput.focus()
        editInput.select()
        console.log('✅ Input focused and selected for index:', index)
        
        // Ensure the input is truly ready for editing
        editInput.setSelectionRange(0, editInput.value.length)
      }, 100) // Slightly longer delay to ensure proper focus
    }
  }

  private saveCoordinateEdit(index: number, newValue: string): void {
    console.log('💾 saveCoordinateEdit called for index:', index, 'newValue:', newValue)
    
    const coordinateItem = this.container.querySelector(`[data-index="${index}"].coordinate-item`)
    const editButton = coordinateItem?.querySelector('.edit-coordinate-btn') as HTMLButtonElement
    const editInput = coordinateItem?.querySelector('.coordinate-edit-input') as HTMLInputElement
    const displayText = coordinateItem?.querySelector('.font-medium')
    const distanceText = coordinateItem?.querySelector('.text-sm')

    if (!editButton || !editInput || !displayText || !distanceText) {
      console.error('❌ Missing elements for saveCoordinateEdit, index:', index)
      return
    }

    // Update coordinates in app state
    const coordinates = this.coordinates.slice()
    coordinates[index] = newValue.trim()
    this.coordinates = coordinates
    this.appState.setCoordinates(coordinates)

    console.log('🔄 Exiting edit mode for index:', index)
    
    // Clear active editing flag
    this.activeEditingIndex = null
    
    // Exit edit mode
    editInput.classList.add('hidden')
    displayText.classList.remove('hidden')
    distanceText.classList.remove('hidden')
    
    // Restore button styling
    editButton.innerHTML = `
      <span class="flex items-center space-x-1">
        <span>✏️</span>
        <span class="font-medium">Edit</span>
      </span>
    `
    editButton.classList.add('bg-emerald-500', 'hover:bg-emerald-600')
    editButton.classList.remove('bg-blue-500', 'hover:bg-blue-600')
    editButton.title = 'Edit this coordinate'

    // Update the display text immediately without full render
    const parsed = parseCoordinate(newValue.trim())
    const displayElement = coordinateItem?.querySelector('.font-medium')
    const distanceElement = coordinateItem?.querySelector('.text-sm')
    
    if (displayElement) {
      displayElement.textContent = this.formatBearing(parsed)
    }
    if (distanceElement) {
      distanceElement.textContent = this.getCoordinateDistance(parsed)
    }

    console.log('🔄 Updating plot only (not full render) for index:', index)
    
    // Only refresh the plot SVG, not the entire component
    this.updatePlotOnly()

    this.toastService.success('Coordinate Updated', `Entry ${index + 1} has been updated and the plot refreshed`)
  }

  private updatePlotOnly(): void {
    // Only update the plot SVG without re-rendering the entire component
    const plotCanvas = this.container.querySelector('#plot-canvas') as SVGSVGElement
    if (plotCanvas) {
      // Clear existing plot content
      plotCanvas.innerHTML = ''
      
      // Redraw the plot
      this.drawPlot()
      
      // Ensure zoom and pan state is preserved
      this.updateTransform()
      this.updateZoomDisplay()
      
      // Re-setup label dragging for the new plot
      this.setupLabelDragging()
    }
  }

  private cancelCoordinateEdit(index: number): void {
    const coordinateItem = this.container.querySelector(`[data-index="${index}"].coordinate-item`)
    const editButton = coordinateItem?.querySelector('.edit-coordinate-btn') as HTMLButtonElement
    const editInput = coordinateItem?.querySelector('.coordinate-edit-input') as HTMLInputElement
    const displayText = coordinateItem?.querySelector('.font-medium')
    const distanceText = coordinateItem?.querySelector('.text-sm')

    if (!editButton || !editInput || !displayText || !distanceText) return

    // Reset input to original value
    editInput.value = this.coordinates[index]

    // Clear active editing flag
    this.activeEditingIndex = null

    // Exit edit mode without saving
    editInput.classList.add('hidden')
    displayText.classList.remove('hidden')
    distanceText.classList.remove('hidden')
    
    // Restore button styling
    editButton.innerHTML = `
      <span class="flex items-center space-x-1">
        <span>✏️</span>
        <span class="font-medium">Edit</span>
      </span>
    `
    editButton.classList.add('bg-emerald-500', 'hover:bg-emerald-600')
    editButton.classList.remove('bg-blue-500', 'hover:bg-blue-600')
    editButton.title = 'Edit this coordinate'
  }
} 